import {
  useState, useCallback, useEffect, useRef,
} from 'react';

/**
 * Stable hook for managing modals synced with URL query params.
 *
 * @param {Object} config - Modal config (keys are modal names, values: { isIdParam: boolean })
 * Example:
 * {
 *   addRoute: { isIdParam: false },
 *   noteId: { isIdParam: true },
 *   editUser: { isIdParam: true }
 * }
 */
export function useUrlModals(config) {
  const configRef = useRef(config); // stores stable reference
  const [modals, setModals] = useState({});

  // Initializes state from URL only once
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const initialState = {};

    Object.keys(configRef.current).forEach(key => {
      const modalConfig = configRef.current[key];

      initialState[key] = urlParams.has(key)
        ? { isOpen: true, value: modalConfig.isIdParam ? urlParams.get(key) : null }
        : { isOpen: false, value: null };
    });

    setModals(initialState);
  }, []);

  // Opens modal
  const openModal = useCallback((key, value = '1') => {
    setModals(prev => ({
      ...prev,
      [key]: {
        isOpen: true,
        value: configRef.current[key].isIdParam ? value : null,
      },
    }));

    const url = new URL(window.location);
    url.searchParams.set(key, String(value));
    window.history.replaceState({}, '', url);
  }, []);

  // Closes modal
  const closeModal = useCallback((key) => {
    setModals(prev => ({
      ...prev,
      [key]: { isOpen: false, value: null },
    }));

    const url = new URL(window.location);
    url.searchParams.delete(key);
    window.history.replaceState({}, '', url);
  }, []);

  return { modals, openModal, closeModal };
}
